import { useState } from 'react';
import kit from './kit.module.css';
import styles from './HumanInTheLoopDemo.module.css';

type State = 'review' | 'success' | 'cancelled';

function formatTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function Skeleton({ widths }: { widths: number[] }) {
  return (
    <div className={styles.lines}>
      {widths.map((w, i) => (
        <span key={i} className={styles.line} style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

export function HumanInTheLoopDemo() {
  const [state, setState] = useState<State>('review');
  const [generatedAt, setGeneratedAt] = useState('');

  function handleApprove() {
    setGeneratedAt(formatTime());
    setState('success');
  }

  return (
    <div className={kit.root}>
      <div className={kit.message}>Generate a CV for Sarah Mitchell from the details I shared.</div>

      {state === 'review' && (
        <>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>
              I'm about to generate Sarah's CV with her work history, skills and a personal
              statement. Check the outline first - nothing is created until you approve.
            </p>
          </div>

          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>Review before generating</span>
              <span>CV preview</span>
            </div>
            <h3 className={kit.proposalTitle}>Sarah Mitchell</h3>
            <dl className={`${kit.fields} ${styles.fields}`}>
              <dt>Work history</dt>
              <dd><Skeleton widths={[92, 74, 48]} /></dd>
              <dt>Skills</dt>
              <dd><Skeleton widths={[70, 44]} /></dd>
              <dt>Statement</dt>
              <dd><Skeleton widths={[96, 88, 60]} /></dd>
            </dl>
            <div className={`${kit.row} ${styles.actions}`}>
              <button type="button" className={`${kit.btn} ${kit.btnApprove}`} onClick={handleApprove}>
                Approve &amp; generate
              </button>
              <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => setState('cancelled')}>
                Edit before generating
              </button>
            </div>
          </div>
          <p className={kit.feedback}>You can download and edit the final CV at any time.</p>
        </>
      )}

      {state === 'success' && (
        <>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>Sarah's CV is ready to download.</p>
          </div>
          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>Generated</span>
              <span>{generatedAt}</span>
            </div>
            <h3 className={kit.proposalTitle}>CV generated successfully</h3>
            <div className={kit.row}>
              <span className={`${kit.chip} ${kit.chipGood}`}>✓ Approved by you</span>
              <span className={kit.chip}>Ready to download</span>
            </div>
          </div>
          <p className={kit.feedback} aria-live="polite">Logged at {generatedAt}, after your approval.</p>
        </>
      )}

      {state === 'cancelled' && (
        <>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>No problem. I've paused here and haven't generated anything.</p>
          </div>
          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>Edit mode</span>
              <span>Nothing generated</span>
            </div>
            <h3 className={kit.proposalTitle}>Returned to edit mode</h3>
            <p className={styles.body}>Update the information, then send it back for review.</p>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => setState('review')}>
              Back to review
            </button>
          </div>
          <p className={kit.feedback} aria-live="polite">No CV was generated.</p>
        </>
      )}
    </div>
  );
}
